import { useEffect, useState } from 'react';
import React from 'react';
import { Modal, Button, Spinner, Form, Row, Col } from 'react-bootstrap';

import { useAdminGetImage, useCreateImage, useModifyImage } from '../../api-new';
import {
  getDefaultImageFormData,
  getImageFormData,
  getImageFormFields,
  getImageRequestPayload,
  validateImageFormData,
} from '../../utils/imageQuestionEditUtil';
import AlertDismissible from './AlertDismissible';
import BookModalLink from './BookModalLink';
import Translated from './Translated';

const ImageQuestionContentModal = ({ questionId, onClose, onCloseAfterUpdate }) => {
  const editMode = Boolean(questionId);
  const formFields = getImageFormFields();
  const defaultFormData = getDefaultImageFormData();
  const [formData, setFormData] = useState(defaultFormData);
  const [modifiedFields, setModifiedFields] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [validationMessageVisible, setValidationMessageVisible] = useState(false);

  const {
    data: questionData,
    isLoading: questionLoading,
    isError: fetchError,
  } = useAdminGetImage(questionId);

  const {
    mutate: updateImage,
    isPending: updatePending,
    isSuccess: updateSuccessful,
    isError: updateError,
  } = useModifyImage();

  const {
    mutate: createImage,
    isPending: createPending,
    isSuccess: createSuccessful,
    isError: createError,
  } = useCreateImage();

  useEffect(() => {
    if (questionData) {
      setFormData(getImageFormData(questionData));
    }
  }, [questionData]);

  const handleInputChange = (e) => {
    const { name, type, checked, value } = e.target;
    const newFormData = {
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    };

    setFormData(newFormData);
    setModifiedFields((prev) => (prev.includes(name) ? prev : [...prev, name]));
  };

  const handleSubmit = () => {
    const { isValid, errors } = validateImageFormData(formData);
    if (!isValid) {
      setValidationErrors(errors);
      setValidationMessageVisible(!validationMessageVisible);
      return;
    }

    setValidationErrors({});
    const imageQuestion = getImageRequestPayload(formData);

    const action = editMode ? updateImage : createImage;

    action(imageQuestion, {
      onSuccess: () => {
        setModifiedFields([]);
        if (!editMode) {
          setFormData(defaultFormData);
        }
      },
    });
  };

  const handleClose = () => {
    if (updateSuccessful || createSuccessful) {
      onCloseAfterUpdate();
    }
    onClose();
  };

  const renderField = (field) => {
    const isModified = modifiedFields.includes(field.name);

    switch (field.type) {
      case 'text':
        return (
          <div>
            <Form.Control
              type="text"
              name={field.name}
              value={formData[field.name]}
              disabled={field.disabled}
              onChange={handleInputChange}
            />
            {isModified && <span className="badge bg-warning"><Translated translationKey={'modified'} /></span>}
          </div>
        );
      case 'checkbox':
        return (
          <Col xs="auto">
            <Form.Check
              className="fw-bold"
              type="checkbox"
              id={field.name}
              label={field.label}
              name={field.name}
              checked={formData[field.name]}
              disabled={field.disabled}
              onChange={handleInputChange}
            />
            {isModified && <span className="badge bg-warning"><Translated translationKey={'modified'} /></span>}
          </Col>
        );
      default:
        return null;
    }
  };

  const renderBookRefs = (refs) => {
    return refs.flatMap((ref) =>
      ref.languages.map((lang) => (
        <div className="d-flex align-items-center gap-2" key={`${ref.id}-${lang}`}>
          <label className="fw-bold">{lang}</label>
          <BookModalLink lang={lang} contentId={ref.id} showId />
        </div>
      ))
    );
  };

  const fetchPending = questionLoading ? <Spinner animation="border" /> : null;

  const fetchErrorAlert = fetchError ? (
    <AlertDismissible variant="danger" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
      <Translated translationKey={'error_fetching_data'} />
    </AlertDismissible>
  ) : null;

  const successfulUpdateAlert = updateSuccessful ? (
    <AlertDismissible variant="success" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
      <Translated translationKey={'updated_successfully'} />
    </AlertDismissible>
  ) : null;

  const errorUpdateAlert = updateError ? (
    <AlertDismissible variant="danger" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
      <Translated translationKey={'update_failed'} />
    </AlertDismissible>
  ) : null;

  const successfulCreateAlert = createSuccessful ? (
    <AlertDismissible variant="success" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
      <Translated translationKey={'image_create_success'} />
    </AlertDismissible>
  ) : null;

  const errorCreateAlert = createError ? (
    <AlertDismissible variant="danger" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
      <Translated translationKey={'image_create_fail'} />
    </AlertDismissible>
  ) : null;

  const validationErrorAlert = Object.keys(validationErrors).length > 0 ? (
    <AlertDismissible
      variant="danger"
      style={{ position: 'sticky', top: 0, zIndex: 10 }}
      key={validationMessageVisible}
    >
      <div>
        <strong><Translated translationKey={'validation_errors'} />:</strong>
        <ul className="mb-0">
          {Object.entries(validationErrors).map(([field, translationKey]) => (
            <li key={field}><Translated translationKey={translationKey} /></li>
          ))}
        </ul>
      </div>
    </AlertDismissible>
  ) : null;

  return (
    <Modal show onHide={handleClose} size="lg" backdrop="static" keyboard={false}>
      <Modal.Header closeButton>
        <Modal.Title><Translated translationKey={editMode ? 'edit' : 'create_image'} /></Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {fetchPending}
        {fetchErrorAlert}
        {validationErrorAlert}
        {successfulUpdateAlert}
        {errorUpdateAlert}
        {successfulCreateAlert}
        {errorCreateAlert}

        {formData && (
          <>
            <Form>
              <Form.Group as={Row} className="mb-4">
                {formFields
                  .filter((field) => field.type === 'checkbox' && !field.group)
                  .map((field) => (
                    <React.Fragment key={field.name}>
                      {renderField(field)}
                    </React.Fragment>
                  ))}
              </Form.Group>

              <div className="mb-2">
                <label className="fw-bold">
                  <Translated translationKey={'license_class'} />:
                </label>
              </div>

              <Form.Group as={Row} className="mb-4">
                {formFields
                  .filter((field) => field.type === 'checkbox' && field.group === 'category')
                  .map((field) => (
                    <React.Fragment key={field.name}>
                      {renderField(field)}
                    </React.Fragment>
                  ))}
              </Form.Group>

              {formFields
                .filter((field) => field.type !== 'checkbox')
                .map((field) => (
                  <Form.Group key={field.name} className="mb-2">
                    <Form.Label className="fw-bold">{field.label}</Form.Label>
                    {renderField(field)}
                  </Form.Group>
                ))}
            </Form>

            {questionData?.bookReferences?.length > 0 && (
              <div className="mb-4">
                <Translated translationKey={'book_references'} />:
                {renderBookRefs(questionData.bookReferences)}
              </div>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          <Translated translationKey={'cancel'} />
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={questionLoading || updatePending || createPending}>
          <Translated translationKey={'save'} />
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ImageQuestionContentModal;